/**
 * Die Platzhalter der Textbausteine — eine Tabelle, aus der Automatik, Karte
 * und Freigabe dieselben Worte lesen (Plan
 * docs/baustellenordnung-assistenz-plan.md, Stufe B1, Falle F3).
 *
 * ═══ WARUM EINE TABELLE ═══════════════════════════════════════════════════
 * Die 83 Bausteine der Vorlage (`docs/baustellenordnung/02-textbaustein-
 * katalog.md`) tragen Platzhalter `{name}`. Hinter jedem Namen stehen drei
 * Fragen, die an verschiedenen Stellen gebraucht werden:
 *   - Woher kommt der Wert? (`quelle` — die Automatik fuellt nur, was ein
 *     Projektdatum belegt; `vorgabe` und `mensch` fuellt sie nie)
 *   - Wie steht er unersetzt im Text? (`kurz` — `[Aushangort]`, damit
 *     `platzhalterIn` ihn findet und die Freigabe anhaelt)
 *   - Wie fragt die Karte danach? (`frage` in Alltagssprache, `vorgabe` als
 *     Vorschlag, den der Mensch mit einem Klick bestaetigt)
 * Stuende das an drei Orten, liefe es auseinander: eine Karte fragte nach
 * „[Meldestelle]", waehrend im Text „[Meldeweg]" stuende.
 *
 * ═══ DIE FORMEN (F3) ══════════════════════════════════════════════════════
 * Katalog `{name}` (maschinell, klein, mit Unterstrich) · unersetzt im Text
 * `[Kurz]` (lesbar, eindeutig) · `kurzZuName` fuehrt zurueck. Ein `[…]`, das
 * niemand aus der Tabelle kennt (von Hand getippt: `[Ort eintragen]`), bleibt
 * mit seinem Text als Name stehen — `platzhalterDefinition` liefert dafuer
 * einen Rueckfall statt eines Fehlers.
 *
 * ═══ DYNAMISCH: DER KOPFSATZ ══════════════════════════════════════════════
 * Sieben Platzhalter (`quelle: 'kopf'`) lesen ein Feld des Kopfsatzes:
 * Erlassender, Unterzeichner, Stand, Revision, Geltungsbeginn, -ende,
 * Anschrift. Diese Felder aendern sich bis zur Freigabe — „Neue Fassung
 * eröffnen" zaehlt die Revision hoch, der Erlassende wird in den Grundlagen
 * korrigiert, die Freigabe setzt die Fassung aus ihrem Eingabefeld. Fest in
 * den Wortlaut geschrieben, stuende danach „Revision 00" unter „Rev. 01".
 * Deshalb bleiben sie als `{name}` im gespeicherten Text stehen, erzeugen
 * keinen offenen Punkt und werden erst beim Bau des Dokuments aus dem
 * Kopfsatz aufgeloest (`automatik.loeseKopfPlatzhalter`). Ein leeres Feld
 * haelt die Freigabe an (`bauordnungLogic.freigabeHindernisse`), nicht der
 * Text.
 *
 * Rein — kein DOM, keine Supabase.
 */

import type { Bauordnung } from '../types';

export type PlatzhalterGruppe = 'projekt' | 'organisation' | 'verantwortliche' | 'notfall' | 'zeiten' | 'orte' | 'regelung';

/**
 * Woher der Wert kommt:
 *  - `kopf`       Kopfsatz der Baustellenordnung (Erlassender, Stand, Geltung)
 *  - `steckbrief` Projektsteckbrief, nur `belegt`e Angaben
 *  - `projekt`    Stammdaten des Projekts (Name)
 *  - `handbuch`   Ordnungsfelder des SiGe-Handbuchs
 *  - `notrufplan` Notrufplan des SiGe-Plans
 *  - `buero`      das eigene Buero (SiGeKo)
 *  - `vorgabe`    kein Datum, aber ein ueblicher Vorschlag — bestaetigt der Mensch
 *  - `mensch`     nur der Mensch weiss es
 */
export type PlatzhalterQuelle = 'kopf' | 'steckbrief' | 'projekt' | 'handbuch' | 'notrufplan' | 'buero' | 'vorgabe' | 'mensch';

export interface PlatzhalterDefinition {
    /** Name ohne Klammern, wie im Katalog: `aushangort`. */
    name: string;
    /** So steht ein unersetzter Platzhalter im Text: `[Aushangort]`. Eindeutig. */
    kurz: string;
    /** Alltagssprache fuer die Karte. */
    frage: string;
    gruppe: PlatzhalterGruppe;
    quelle: PlatzhalterQuelle;
    /** Vorschlag, den der Mensch mit einem Klick bestaetigt. Nur bei `quelle: 'vorgabe'`. */
    vorgabe?: string;
    /** Ein Satz unter der Frage — Beispiel in der Form, die in den Satz passt, oder Warnung der Vorlage. */
    hinweis?: string;
    /**
     * Wert aus dem Kopfsatz, aufgeloest erst beim Druck — bleibt als `{name}`
     * im Wortlaut, nie ein offener Punkt (siehe Kopfkommentar). Nur bei
     * `quelle: 'kopf'`; das Feld nennt `KOPF_FELD_JE_PLATZHALTER`.
     */
    dynamisch?: true;
}

export const GRUPPEN_LABEL: Record<PlatzhalterGruppe, string> = {
    projekt: 'Projektdaten',
    organisation: 'Organisation',
    verantwortliche: 'Verantwortliche',
    notfall: 'Notfall',
    zeiten: 'Zeiten',
    orte: 'Orte',
    regelung: 'Regelungen',
};

const BAULEITUNG = 'die Bauleitung';

/** Kurzschreibweise der Tabelle: Name, Kurz, Frage, Gruppe, Quelle, Zusatz. */
const p = (
    name: string,
    kurz: string,
    frage: string,
    gruppe: PlatzhalterGruppe,
    quelle: PlatzhalterQuelle,
    zusatz: Pick<PlatzhalterDefinition, 'vorgabe' | 'hinweis' | 'dynamisch'> = {},
): PlatzhalterDefinition => ({ name, kurz, frage, gruppe, quelle, ...zusatz });

/**
 * Verantwortliche: mit dem Vorschlag, der auf den meisten Baustellen gilt.
 *
 * Bis 03.11.2026 stand hier für alle „die Bauleitung" (Plan B1). Betreiber:
 * „überall Bauleitung, macht keinen Sinn". Eine Bauleitung stellt keinen
 * Baustrom und reinigt keine Container; das tut, wer die Baustelle
 * einrichtet. Deshalb je Frage die übliche Partei:
 *   - gemeinsame Einrichtung (Strom, Wasser, Zaun, Licht, Sozialräume,
 *     Winterdienst): der Auftraggeber, also der Erlassende, der sie selbst
 *     oder über ein beauftragtes Unternehmen stellt;
 *   - Gewerkeleistung (Wasserhaltung, Seitenschutz): das Gewerk, das sie
 *     üblicherweise erbringt;
 *   - Entsorgung: Verursacherprinzip; Kontrolle: der Ersteller der
 *     Einrichtung; verkehrsrechtliche Anordnung: das ausführende Unternehmen.
 *
 * Jeder Vorschlag passt in alle Sätze des Katalogs, in denen er steht, auch
 * nach „von"/„bei" (`vorgabeImSatz` macht „der" zu „dem") und nach „über"
 * (darum dort ein Neutrum: „über das verursachende Unternehmen").
 */
const v = (name: string, kurz: string, frage: string, vorgabe: string): PlatzhalterDefinition =>
    p(name, kurz, frage, 'verantwortliche', 'vorgabe', { vorgabe });

const AUFTRAGGEBER = 'der Auftraggeber';

/** Kopfsatz: immer dynamisch — eine Quelle `kopf` ohne Aufloesung beim Druck gibt es nicht. */
const k = (name: string, kurz: string, frage: string, hinweis: string): PlatzhalterDefinition =>
    p(name, kurz, frage, 'projekt', 'kopf', { hinweis, dynamisch: true });

/**
 * Alle Platzhalter der Vorlage. Die Fragen sind so gestellt, dass die Antwort
 * in den Satz des Bausteins passt; wo das nicht offensichtlich ist, zeigt
 * `hinweis` ein Beispiel in der passenden Form.
 */
export const PLATZHALTER: readonly PlatzhalterDefinition[] = [
    // ─── Projektdaten (fuellt die Automatik, wo es ein Datum gibt) ──────
    // `k`: aus dem Kopfsatz, aufgeloest erst beim Druck. Bezeichnung,
    // Bauherr und SiGeKo haben kein Kopfsatzfeld — sie bleiben statisch.
    k('erlassender', 'Erlassender', 'Wer erlässt die Baustellenordnung?',
        'Bauherr oder Generalunternehmer: wer das Hausrecht hat, nicht das Ingenieurbüro.'),
    k('unterzeichner', 'Unterzeichner', 'Wer unterschreibt für den Erlassenden?',
        'Eine vertretungsberechtigte Person des Erlassenden.'),
    k('stand_datum', 'Stand', 'Welchen Stand trägt diese Fassung?',
        'Datum in der Form TT.MM.JJJJ.'),
    k('revision_nr', 'Revision', 'Welche Revision ist diese Fassung?',
        'z. B. „00"'),
    p('baustelle_bezeichnung', 'Bezeichnung der Baustelle', 'Wie heißt die Baustelle?', 'projekt', 'projekt'),
    k('baustelle_anschrift', 'Anschrift der Baustelle', 'Unter welcher Anschrift liegt die Baustelle?',
        'Die Anschrift steht auch im Notruf. Sie muss für die Rettungsleitstelle auffindbar sein.'),
    k('geltung_beginn', 'Geltungsbeginn', 'Ab wann gilt die Baustellenordnung (Übergabe der Baustelle)?',
        'Datum in der Form TT.MM.JJJJ.'),
    k('geltung_ende', 'Geltungsende', 'Bis wann gilt die Baustellenordnung (Räumung und Rückgabe)?',
        'Datum in der Form TT.MM.JJJJ.'),
    p('bauherr', 'Bauherr', 'Wer ist der Bauherr?', 'projekt', 'steckbrief'),
    p('sigeko', 'SiGeKo', 'Wer ist Koordinator nach Baustellenverordnung in der Ausführung?', 'projekt', 'buero'),
    p('bep_stand', 'Stand BE-Plan', 'Welcher Stand des Baustelleneinrichtungsplans gilt?', 'projekt', 'mensch',
        { hinweis: 'z. B. „vom 15.10.2026" oder „Index B"' }),

    // ─── Organisation ───────────────────────────────────────────────────
    p('bauleitung', 'Bauleitung', 'Wer stellt die Bauleitung (Firma und Name)?', 'organisation', 'mensch'),
    p('telefon_bauleitung', 'Telefon Bauleitung', 'Unter welcher Telefonnummer ist die Bauleitung erreichbar?', 'organisation', 'mensch'),
    p('meldestelle', 'Meldestelle', 'An wen werden Unfälle und Beinaheunfälle gemeldet?', 'organisation', 'vorgabe',
        { vorgabe: BAULEITUNG }),
    p('anmeldestelle', 'Anmeldestelle', 'Wo melden sich Personen beim Betreten der Baustelle an?', 'organisation', 'vorgabe',
        { vorgabe: 'das Baubüro der Bauleitung' }),
    p('planstelle', 'Einsicht Bestandspläne', 'Wo können Bestandsunterlagen und Leitungspläne eingesehen werden?', 'organisation', 'vorgabe',
        { vorgabe: BAULEITUNG }),
    p('logistikstelle', 'Anmeldung Anlieferung', 'Bei wem werden Anlieferungen angemeldet?', 'organisation', 'vorgabe',
        { vorgabe: BAULEITUNG }),
    p('freigabestelle', 'Freigabe Heißarbeiten', 'Wer gibt Arbeiten mit Brandgefahr (Schweißen, Trennen, Löten) frei?', 'organisation', 'vorgabe',
        { vorgabe: BAULEITUNG }),
    p('abstimmperson', 'Person für die Abstimmung', 'Wen bestimmt der Auftraggeber für die Abstimmung der Arbeiten mehrerer Firmen?', 'organisation', 'mensch',
        { hinweis: 'Die Befugnis dieser Person ist begrenzt: Ort, Zeit und Reihenfolge der Arbeiten, Sperrbereiche, gemeinsame Einrichtungen.' }),
    p('kran_betreiber', 'Kranbetreiber', 'Wer betreibt den Kran?', 'organisation', 'mensch'),
    p('bauaufzug_betreiber', 'Betreiber Bauaufzug', 'Wer betreibt den Bauaufzug?', 'organisation', 'mensch'),
    p('kranliste', 'Kranliste', 'Welche Krane überschneiden sich in ihren Schwenkbereichen?', 'organisation', 'mensch',
        { hinweis: 'z. B. „K1 und K2"' }),
    p('anlage_organigramm', 'Anlage Organigramm', 'In welcher Anlage steht die Liste der Beteiligten mit Erreichbarkeiten?', 'organisation', 'mensch',
        { hinweis: 'z. B. „2"' }),

    // ─── Verantwortliche (Vorschlag je Frage, siehe `v`) ────────────────
    v('baustrom_verantwortlich', 'Verantwortlich Baustrom', 'Wer stellt die Baustromversorgung bereit?', AUFTRAGGEBER),
    v('winterdienst_verantwortlich', 'Verantwortlich Winterdienst', 'Wer räumt und streut die gemeinsam genutzten Wege?', AUFTRAGGEBER),
    v('wasserhaltung_verantwortlich', 'Verantwortlich Wasserhaltung', 'Wer betreibt die Wasserhaltung?', 'das Unternehmen für die Erdarbeiten'),
    v('verkehrsrecht_verantwortlich', 'Verantwortlich Verkehrsrecht', 'Wer holt die verkehrsrechtliche Anordnung ein?', 'das ausführende Unternehmen'),
    v('sicherung_verantwortlich', 'Verantwortlich Baustellensicherung', 'Wer stellt Bauzaun, Zugangssicherung und Beschilderung?', AUFTRAGGEBER),
    v('reinigung_verantwortlich', 'Verantwortlich Reinigung', 'Wer reinigt und wartet die Sanitär- und Pausenräume?', AUFTRAGGEBER),
    v('kontrolle_verantwortlich', 'Verantwortlich Kontrolle', 'Wer kontrolliert die gemeinsam genutzten Schutzeinrichtungen gegen Absturz?', 'der Ersteller der jeweiligen Einrichtung'),
    v('entsorgung_verantwortlich', 'Verantwortlich Entsorgung', 'Wer entsorgt gesammelte Reste wie Betonwaschwasser, Farben und Öle?', 'das verursachende Unternehmen'),
    v('beleuchtung_verantwortlich', 'Verantwortlich Beleuchtung', 'Wer stellt die Allgemeinbeleuchtung der Wege bereit?', AUFTRAGGEBER),
    v('bauwasser_verantwortlich', 'Verantwortlich Bauwasser', 'Bei wem wird die Entnahme von Bauwasser angemeldet?', AUFTRAGGEBER),
    v('absturzsicherung_verantwortlich', 'Verantwortlich Absturzsicherung', 'Wer stellt den Seitenschutz in den gemeinsam genutzten Bereichen?', 'das Unternehmen für den Rohbau'),

    // ─── Notfall ────────────────────────────────────────────────────────
    p('sammelplatz', 'Sammelplatz', 'Wo ist der Sammelplatz nach einer Räumung?', 'notfall', 'handbuch'),
    p('krankenhaus', 'Krankenhaus', 'Welches Krankenhaus mit Notaufnahme liegt am nächsten?', 'notfall', 'notrufplan'),
    p('durchgangsarzt', 'Durchgangsarzt', 'Welcher Durchgangsarzt ist zuständig?', 'notfall', 'notrufplan'),
    p('durchgangsarzt_anschrift', 'Anschrift Durchgangsarzt', 'Unter welcher Anschrift liegt die Praxis des Durchgangsarztes?', 'notfall', 'notrufplan'),
    p('rettungszufahrt', 'Rettungszufahrt', 'Über welche Zufahrt kommen Rettungskräfte auf die Baustelle?', 'notfall', 'mensch',
        { hinweis: 'z. B. „das Tor 1 an der Musterstraße"' }),
    p('lotsenpunkt', 'Lotsenpunkt', 'Wo werden Rettungskräfte in Empfang genommen?', 'notfall', 'mensch',
        { hinweis: 'z. B. „der Einfahrt Tor 1"' }),
    p('alarmsignal', 'Alarmsignal', 'Womit wird die Räumung der Baustelle ausgelöst?', 'notfall', 'mensch',
        { hinweis: 'z. B. „ein Dauersignal der Sirene am Baubüro"' }),
    p('erste_hilfe_raum', 'Erste-Hilfe-Raum', 'Wo ist der Erste-Hilfe-Raum eingerichtet?', 'notfall', 'mensch',
        { hinweis: 'z. B. „Container 3 am Baubüro"' }),
    p('erste_hilfe_punkte', 'Erste-Hilfe-Punkte', 'Wo steht Erste-Hilfe-Material bereit?', 'notfall', 'mensch'),
    p('loeschmittel_standorte', 'Standorte Feuerlöscher', 'Wo stehen die Feuerlöscher?', 'notfall', 'mensch'),
    p('fluchtwege', 'Fluchtwege', 'Wo verlaufen die Flucht- und Rettungswege im Bauwerk?', 'notfall', 'mensch'),
    p('probealarm_intervall', 'Probealarm', 'Wie oft wird der Alarm geprobt?', 'notfall', 'mensch',
        { hinweis: 'z. B. „drei Monaten"' }),
    p('aushang_ersthelfer', 'Aushang Ersthelfer', 'Wo hängt die Liste der anwesenden Ersthelfer aus?', 'notfall', 'mensch',
        { hinweis: 'z. B. „im Baubüro"' }),

    // ─── Zeiten ─────────────────────────────────────────────────────────
    p('arbeitszeit_beginn', 'Arbeitsbeginn', 'Ab wann ist die Baustelle in Betrieb?', 'zeiten', 'handbuch',
        { hinweis: 'z. B. „7:00 Uhr"' }),
    p('arbeitszeit_ende', 'Arbeitsende', 'Bis wann ist die Baustelle in Betrieb?', 'zeiten', 'handbuch',
        { hinweis: 'z. B. „17:00 Uhr"' }),
    p('arbeitstage', 'Arbeitstage', 'An welchen Tagen ist die Baustelle in Betrieb?', 'zeiten', 'handbuch',
        { hinweis: 'z. B. „den Werktagen Montag bis Freitag"' }),
    p('vorlauf_sonderzeit', 'Vorlauf Sonderzeiten', 'Wie lange vorher sind Arbeiten außerhalb der Betriebszeit zu beantragen?', 'zeiten', 'mensch',
        { hinweis: 'z. B. „zwei Werktage"' }),
    p('anlieferzeit_beginn', 'Anlieferung ab', 'Ab wann sind Anlieferungen möglich?', 'zeiten', 'mensch',
        { hinweis: 'z. B. „7:00 Uhr"' }),
    p('anlieferzeit_ende', 'Anlieferung bis', 'Bis wann sind Anlieferungen möglich?', 'zeiten', 'mensch',
        { hinweis: 'z. B. „15:00 Uhr"' }),
    p('vorlauf_anlieferung', 'Vorlauf Anlieferung', 'Wie lange vorher werden Anlieferungen angemeldet?', 'zeiten', 'mensch',
        { hinweis: 'z. B. „24 Stunden"' }),
    p('vorlauf_abschaltung', 'Vorlauf Abschaltung', 'Wie lange vorher werden Stromabschaltungen angekündigt?', 'zeiten', 'mensch',
        { hinweis: 'z. B. „einen Arbeitstag"' }),
    p('winterdienst_beginn', 'Beginn Winterdienst', 'Ab wann läuft der Winterdienst?', 'zeiten', 'mensch',
        { hinweis: 'z. B. „1. November"' }),
    p('kontrollintervall', 'Kontrollintervall', 'Wie oft werden die gemeinsam genutzten Schutzeinrichtungen kontrolliert?', 'zeiten', 'mensch',
        { hinweis: 'z. B. „einer Woche"' }),

    // ─── Orte ───────────────────────────────────────────────────────────
    p('aushangort', 'Aushangort', 'Wo liegt die Baustellenordnung im Baubüro zur Einsicht aus?', 'orte', 'mensch',
        { hinweis: 'z. B. „im Container 1 am Haupttor"' }),
    p('raucherbereich', 'Raucherbereich', 'Wo ist die gekennzeichnete Raucherfläche?', 'orte', 'mensch'),
    p('pausenraum_standort', 'Pausenräume', 'Wo stehen die Pausenräume?', 'orte', 'mensch'),
    p('sanitaer_standort', 'Sanitärräume', 'Wo stehen Sanitär- und Waschräume?', 'orte', 'mensch'),
    p('baustrom_standorte', 'Baustromverteiler', 'Wo stehen die Baustromverteiler?', 'orte', 'mensch'),
    p('bauwasser_standorte', 'Bauwasser-Entnahmestellen', 'Wo wird Bauwasser entnommen?', 'orte', 'mensch'),
    p('abfall_standorte', 'Abfallbehälter', 'Wo stehen die Abfallbehälter?', 'orte', 'mensch'),
    p('gefahrstoff_lagerflaeche', 'Gefahrstofflager', 'Wo dürfen Kraftstoffe, Öle und Lösemittel gelagert werden?', 'orte', 'mensch',
        { hinweis: 'z. B. „der Lagerfläche neben Container 2"' }),
    p('zufahrt', 'Zufahrt', 'Über welche Zufahrt wird die Baustelle befahren?', 'orte', 'mensch'),
    p('abfahrt', 'Abfahrt', 'Über welche Ausfahrt wird die Baustelle verlassen?', 'orte', 'mensch'),
    p('anfahrtsrichtung', 'Anfahrtsrichtung', 'Aus welcher Richtung ist der Lotsenpunkt anzufahren?', 'orte', 'mensch',
        { hinweis: 'z. B. „Musterstraße"' }),
    p('mischverkehrsbereich', 'Mischverkehrsfläche', 'Wo teilen sich Fahrzeuge und Fußgänger den Weg?', 'orte', 'mensch'),
    p('bauwerkszugaenge', 'Zugänge zum Bauwerk', 'Über welche Zugänge wird das Bauwerk betreten?', 'orte', 'mensch'),
    p('schmutzwasser_anschluss', 'Schmutzwasseranschluss', 'Wohin wird Schmutzwasser abgeleitet?', 'orte', 'mensch'),
    p('niederschlagswasser_weg', 'Ableitung Niederschlagswasser', 'Wie wird Niederschlags- und Sickerwasser abgeführt?', 'orte', 'mensch'),
    p('bauaufzug_standort', 'Standort Bauaufzug', 'Wo steht der Bauaufzug?', 'orte', 'mensch'),

    // ─── Regelungen (Entscheidungen des Auftraggebers) ──────────────────
    p('tempolimit', 'Höchstgeschwindigkeit', 'Welche Höchstgeschwindigkeit gilt auf dem Gelände?', 'regelung', 'mensch',
        { hinweis: 'z. B. „10 km/h"' }),
    p('psa_grenze', 'Beginn der Tragepflicht', 'Ab wo gilt die Tragepflicht für Helm, Sicherheitsschuhe und Warnkleidung?', 'regelung', 'mensch',
        { hinweis: 'z. B. „dem Durchschreiten des Bauzauns"' }),
    p('abfallfraktionen', 'Abfallfraktionen', 'Für welche Abfallarten stehen Behälter bereit?', 'regelung', 'mensch',
        { hinweis: 'z. B. „Bauschutt, Holz, Metall und gemischte Bauabfälle"' }),
    p('kran_vorfahrtregel', 'Vorfahrt der Krane', 'Welcher Kran hat in den gemeinsamen Feldern Vorfahrt?', 'regelung', 'mensch',
        { hinweis: 'Festlegung des Auftraggebers.' }),
    p('kran_funkkanal', 'Funkkanal Krane', 'Über welchen Funkkanal sprechen sich die Kranführer ab?', 'regelung', 'mensch'),
    p('bauwasser_abrechnung', 'Abrechnung Bauwasser', 'Wie wird Bauwasser abgerechnet?', 'regelung', 'mensch',
        { hinweis: 'z. B. „Zählerstand je Entnahmestelle"' }),
    p('uebergreifende_gefaehrdungen', 'Übergreifende Gefährdungen', 'Welche Gefährdungen wirken auf dieser Baustelle über das einzelne Gewerk hinaus?', 'regelung', 'mensch',
        { hinweis: 'Nicht aus einer Standardliste füllen. Maßgeblich ist das tatsächliche Zusammentreffen der Arbeiten.' }),
    p('bestandsgefaehrdungen', 'Gefährdungen aus dem Bestand', 'Welche Gefährdungen gehen von Bestand, Leitungen und Umgebung aus?', 'regelung', 'mensch',
        { hinweis: 'Ergebnis der Bestandsermittlung, keine Standardliste.' }),
    p('beschraenkende_einfluesse', 'Beschränkende Einflüsse', 'Welche Einflüsse beschränken den Bauablauf?', 'regelung', 'mensch',
        { hinweis: 'z. B. „der laufende Schulbetrieb und die Ruhezeiten der Nachbarschaft"' }),
    p('ablage_uebergaben', 'Ablage Übergabeprotokolle', 'Wo legt die Bauleitung die Übergabeprotokolle der Schutzeinrichtungen ab?', 'regelung', 'mensch',
        { hinweis: 'z. B. „dem Ordner Übergaben im Baubüro"' }),
    p('ablage_kenntnisnahme', 'Ablage Kenntnisnahmen', 'Wo legt die Bauleitung die unterschriebenen Kenntnisnahmen ab?', 'regelung', 'mensch',
        { hinweis: 'z. B. „dem Ordner Kenntnisnahmen im Baubüro"' }),
];

const NACH_NAME = new Map(PLATZHALTER.map(d => [d.name, d]));
const NACH_KURZ = new Map(PLATZHALTER.map(d => [d.kurz.toLowerCase(), d.name]));

// ═══════════════════════════════════════════════════════════════════════════
// DYNAMISCHE PLATZHALTER — Kopfsatz, aufgeloest beim Druck
// ═══════════════════════════════════════════════════════════════════════════

/** Die Namen der dynamischen Platzhalter, in Tabellenreihenfolge. */
export const DYNAMISCHE_PLATZHALTER: readonly string[] = PLATZHALTER.filter(d => d.dynamisch).map(d => d.name);

const DYNAMISCH = new Set(DYNAMISCHE_PLATZHALTER);

/** Bleibt `{name}` im Wortlaut stehen und wird erst beim Druck aufgeloest? Unbekannte Namen: nein. */
export const istDynamisch = (name: string): boolean => DYNAMISCH.has(name);

/** Die Kopfsatzfelder, aus denen dynamische Platzhalter gelesen werden. */
export type KopfFeld = Extract<keyof Bauordnung,
    'erlassender_firma' | 'erlassender_name' | 'stand' | 'version_label' | 'geltung_von' | 'geltung_bis' | 'geltung_ort'>;

/**
 * Je dynamischem Platzhalter das Feld des Kopfsatzes. Der Druck liest es
 * (`loeseKopfPlatzhalter`), die Freigabe prueft es auf Inhalt. Der Test
 * haelt fest, dass die Schluessel genau `DYNAMISCHE_PLATZHALTER` sind.
 */
export const KOPF_FELD_JE_PLATZHALTER: Readonly<Record<string, KopfFeld>> = {
    erlassender: 'erlassender_firma',
    unterzeichner: 'erlassender_name',
    stand_datum: 'stand',
    revision_nr: 'version_label',
    geltung_beginn: 'geltung_von',
    geltung_ende: 'geltung_bis',
    baustelle_anschrift: 'geltung_ort',
};

/** `foo_bar` → `Foo bar`; ein schon lesbarer Text bleibt, nur der erste Buchstabe wird gross. */
const lesbar = (name: string): string => {
    const text = String(name || '').replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : 'Platzhalter';
};

/**
 * Die Definition zu einem Namen — nie `undefined`.
 *
 * Ein unbekannter Name (von Hand getipptes `[…]`, neuer Baustein ohne Zeile
 * in der Tabelle) bekommt einen Rueckfall: lesbarer Kurztext, Gruppe
 * `regelung`, Quelle `mensch`. So entsteht auch fuer ihn ein offener Punkt
 * mit einer Frage, statt dass er still im Dokument stehen bleibt.
 */
export const platzhalterDefinition = (name: string): PlatzhalterDefinition => {
    const bekannt = NACH_NAME.get(name);
    if (bekannt) return bekannt;
    const kurz = lesbar(name);
    return {
        name,
        kurz,
        frage: `Was gehört an die Stelle „${kurz}“?`,
        gruppe: 'regelung',
        quelle: 'mensch',
    };
};

/** Der Kurztext ohne Klammern — so steht der Platzhalter unersetzt als `[Kurz]` im Text. */
export const nameZuKurz = (name: string): string => platzhalterDefinition(name).kurz;

/**
 * Zurueck vom Kurztext (ohne Klammern) zum Namen — Gross-/Kleinschreibung und
 * Rand egal. Ein Name selbst wird ebenfalls erkannt. `null`, wenn die Tabelle
 * den Text nicht kennt.
 */
export const kurzZuName = (kurz: string): string | null => {
    const schluessel = String(kurz || '').trim();
    if (!schluessel) return null;
    if (NACH_NAME.has(schluessel)) return schluessel;
    return NACH_KURZ.get(schluessel.toLowerCase()) ?? null;
};

/**
 * Praepositionen, nach denen ein Vorschlag im Dativ stehen muss. Die Vorlage
 * benutzt denselben Platzhalter in verschiedenen Faellen — `{baustrom_
 * verantwortlich}` einmal als Subjekt („stellt … bereit"), einmal nach „im
 * Auftrag von". „von die Bauleitung" stuende sonst in einem unterschriebenen
 * Dokument.
 */
const DATIV = new Set(['bei', 'von', 'mit', 'aus', 'nach', 'zu', 'seit']);

const DATIV_ARTIKEL: ReadonlyArray<readonly [RegExp, string]> = [
    [/^die\s/i, 'der '],
    [/^das\s/i, 'dem '],
    [/^der\s/i, 'dem '],
];

const escape = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Der Vorschlag eines Platzhalters, passend zum Satz, in dem er steht.
 *
 * Steht vor dem Platzhalter eine Praeposition mit Dativ („bei", „von" …),
 * wird der Artikel angepasst: „die Bauleitung" → „der Bauleitung". `undefined`,
 * wenn der Platzhalter keinen Vorschlag hat.
 */
export const vorgabeImSatz = (name: string, wortlaut: string): string | undefined => {
    const d = platzhalterDefinition(name);
    if (!d.vorgabe) return undefined;
    const muster = new RegExp(`(\\S+)\\s+(?:\\{${escape(name)}\\}|\\[${escape(d.kurz)}\\])`, 'i');
    const treffer = muster.exec(String(wortlaut || ''));
    const davor = (treffer?.[1] || '').toLowerCase().replace(/[^a-zäöüß]/g, '');
    if (!DATIV.has(davor)) return d.vorgabe;
    for (const [artikel, ersatz] of DATIV_ARTIKEL) {
        if (artikel.test(d.vorgabe)) return d.vorgabe.replace(artikel, ersatz);
    }
    return d.vorgabe;
};
